import React, { createContext, useContext, useState, useMemo, useEffect } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material';

const ThemeContext = createContext();

export function useThemeMode() {
  return useContext(ThemeContext);
}

const getDesignTokens = (mode) => ({
  palette: {
    mode,
    primary: {
      main: mode === 'light' ? '#1976d2' : '#90caf9',
      light: mode === 'light' ? '#42a5f5' : '#e3f2fd',
      dark: mode === 'light' ? '#1565c0' : '#42a5f5',
    },
    secondary: {
      main: mode === 'light' ? '#dc004e' : '#f48fb1',
    },
    success: {
      main: mode === 'light' ? '#2e7d32' : '#66bb6a',
      light: mode === 'light' ? '#4caf50' : '#81c784',
    },
    warning: {
      main: mode === 'light' ? '#ed6c02' : '#ffa726',
      light: mode === 'light' ? '#ff9800' : '#ffb74d',
    },
    error: {
      main: mode === 'light' ? '#d32f2f' : '#f44336',
      light: mode === 'light' ? '#ef5350' : '#e57373',
    },
    background: {
      default: mode === 'light' ? '#f5f5f5' : '#0a1929',
      paper: mode === 'light' ? '#ffffff' : '#132f4c',
    },
    text: {
      primary: mode === 'light' ? 'rgba(0, 0, 0, 0.87)' : '#ffffff',
      secondary: mode === 'light' ? 'rgba(0, 0, 0, 0.6)' : 'rgba(255, 255, 255, 0.7)',
    },
    divider: mode === 'light' ? 'rgba(0, 0, 0, 0.12)' : 'rgba(255, 255, 255, 0.12)',
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h4: { fontWeight: 600 },
    h5: { fontWeight: 500 },
    h6: { fontWeight: 500 },
  },
  components: {
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 12,
          boxShadow: mode === 'light'
            ? '0 2px 8px rgba(0,0,0,0.1)'
            : '0 4px 12px rgba(0,0,0,0.5)',
          ...(mode === 'dark' && {
            backgroundColor: '#1e3a5f',
            border: '1px solid rgba(255,255,255,0.1)',
          }),
        },
      },
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 8,
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          borderRadius: 6,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          ...(mode === 'dark' && {
            backgroundImage: 'none',
            backgroundColor: '#1e3a5f',
          }),
        },
        outlined: {
          ...(mode === 'dark' && {
            backgroundColor: '#162d4a',
            borderColor: 'rgba(255,255,255,0.15)',
          }),
        },
      },
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          ...(mode === 'dark' && {
            backgroundColor: '#0d2137',
          }),
        },
      },
    },
    MuiDrawer: {
      styleOverrides: {
        paper: {
          ...(mode === 'dark' && {
            backgroundColor: '#0a1929',
            borderRight: '1px solid rgba(255,255,255,0.1)',
          }),
        },
      },
    },
    MuiListItem: {
      styleOverrides: {
        root: {
          ...(mode === 'dark' && {
            backgroundColor: '#162d4a',
            '&:hover': {
              backgroundColor: '#1e3a5f',
            },
          }),
        },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          ...(mode === 'dark' && {
            '&:hover': {
              backgroundColor: 'rgba(255,255,255,0.08)',
            },
          }),
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          ...(mode === 'dark' && {
            backgroundColor: '#132f4c',
            backgroundImage: 'none',
          }),
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          ...(mode === 'dark' && {
            backgroundColor: '#0d2137',
          }),
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          ...(mode === 'dark' && {
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#0a1929',
              '& fieldset': {
                borderColor: 'rgba(255,255,255,0.2)',
              },
              '&:hover fieldset': {
                borderColor: 'rgba(255,255,255,0.3)',
              },
            },
          }),
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          ...(mode === 'dark' && {
            backgroundColor: '#162d4a',
          }),
        },
        standardInfo: {
          ...(mode === 'dark' && {
            backgroundColor: '#0d47a1',
            color: '#ffffff',
          }),
        },
        standardWarning: {
          ...(mode === 'dark' && {
            backgroundColor: '#e65100',
            color: '#ffffff',
          }),
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        root: {
          ...(mode === 'dark' && {
            backgroundColor: '#0d2137',
          }),
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          ...(mode === 'dark' && {
            color: 'rgba(255,255,255,0.7)',
            '&.Mui-selected': {
              color: '#90caf9',
            },
          }),
        },
      },
    },
    MuiSlider: {
      styleOverrides: {
        root: {
          ...(mode === 'dark' && {
            '& .MuiSlider-rail': {
              backgroundColor: 'rgba(255,255,255,0.2)',
            },
          }),
        },
      },
    },
    MuiToggleButton: {
      styleOverrides: {
        root: {
          ...(mode === 'dark' && {
            color: 'rgba(255,255,255,0.7)',
            borderColor: 'rgba(255,255,255,0.2)',
            '&.Mui-selected': {
              backgroundColor: '#1976d2',
              color: '#ffffff',
            },
          }),
        },
      },
    },
  },
});

export function ThemeProvider({ children }) {
  const [mode, setMode] = useState(() => {
    const saved = localStorage.getItem('themeMode');
    return saved || 'light';
  });

  useEffect(() => {
    localStorage.setItem('themeMode', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode((prev) => (prev === 'light' ? 'dark' : 'light'));
  };

  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  const value = {
    mode,
    toggleTheme,
    setMode,
  };

  return (
    <ThemeContext.Provider value={value}>
      <MuiThemeProvider theme={theme}>
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
}
